import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";
import { useElementRef } from "./use-element-ref";

/** Classes of `Divider`: a hairline in one of three strengths, or a page-colored band. */
export const dividerVariants = cva("shrink-0 border-0", {
  variants: {
    orientation: {
      horizontal: "h-px w-full",
      vertical: "w-px self-stretch",
    },
    tone: {
      subtle: "bg-card-text/10",
      default: "bg-border",
      strong: "bg-gray-scale-300",
    },
    variant: {
      line: "",
      band: "h-3 w-full bg-page",
    },
  },
  defaultVariants: { orientation: "horizontal", tone: "default", variant: "line" },
});

/** Props of `Divider`; decorative (aria-hidden) unless `semantic`. */
export type DividerProps = VariantProps<typeof dividerVariants> & {
  semantic?: boolean;
  className?: string;
};

/** Separator line or band; `semantic` exposes it as a separator to assistive tech. */
export const Divider = forwardRef<HTMLElement, DividerProps>(function Divider(
  { orientation, tone, variant, semantic = false, className },
  ref,
) {
  const elementRef = useElementRef(ref);
  const classes = cn(dividerVariants({ orientation, tone, variant }), className);
  const isVertical = orientation === "vertical" && variant !== "band";

  if (!semantic) return <div ref={elementRef} aria-hidden className={classes} />;
  if (isVertical) return <div ref={elementRef} role="separator" aria-orientation="vertical" className={classes} />;
  return <hr ref={elementRef} className={classes} />;
});
